import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Check, Play } from "lucide-react";
import { api } from "@/lib/api";
import { FadeUp, Overline } from "@/components/FadeUp";
import { Button } from "@/components/ui/button";
import { useLocalizeModel } from "@/i18n/useLocalizeModel";
import {
  Table, TableBody, TableCell, TableRow,
} from "@/components/ui/table";

export default function ModelDetail() {
  const { slug } = useParams();
  const [rawModel, setRawModel] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { t } = useTranslation();
  const localize = useLocalizeModel();
  const model = localize(rawModel);

  useEffect(() => {
    setRawModel(null);
    setNotFound(false);
    api.get(`/models/${slug}`)
      .then((r) => setRawModel(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-32" data-testid="model-not-found">
        <h1 className="text-3xl font-bold text-[#0A2540]">{t("detail.notFound")}</h1>
        <Button asChild className="mt-6 rounded-md bg-[#0A2540] text-white">
          <Link to="/modelle">{t("detail.notFoundBack")}</Link>
        </Button>
      </div>
    );
  }

  if (!model) {
    return <div className="max-w-7xl mx-auto px-6 lg:px-10 py-32 text-slate-500">{t("detail.loading")}</div>;
  }

  return (
    <div data-testid="model-detail-page">
      {/* Hero */}
      <section className="bg-[#EAF0F6] border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-8">
          <Link to="/modelle" data-testid="back-to-models-link"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-[#0052CC]">
            <ArrowLeft className="w-4 h-4" /> {t("detail.back")}
          </Link>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <FadeUp>
            <Overline>{model.category}</Overline>
            <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[#0A2540]">
              {model.name}
            </h1>
            <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">{model.tagline}</p>
            <div className="mt-6 flex items-center gap-4">
              <span className="text-sm font-semibold px-3 py-1.5 border border-slate-300 text-slate-700"
                    data-testid="model-status">{model.status}</span>
              <span className="text-lg font-bold text-[#0A2540]" data-testid="model-price">{model.price_text}</span>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button asChild className="rounded-md bg-[#0A2540] hover:bg-slate-800 text-white px-8 py-3">
                <Link to={`/probefahrt?modell=${model.slug}`} data-testid="detail-test-drive-btn">
                  {t("detail.testDrive")}
                </Link>
              </Button>
              <Button asChild variant="outline"
                      className="rounded-md border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540] hover:text-white px-8 py-3">
                <Link to="/kontakt" data-testid="detail-contact-btn">{t("detail.consult")}</Link>
              </Button>
            </div>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="border border-slate-200 bg-white overflow-hidden">
              <img src={model.image} alt={model.name} data-testid="model-hero-image"
                   style={model.image_position ? { objectPosition: model.image_position } : undefined}
                   className="w-full aspect-[16/10] object-cover" />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Description + Specs */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-5 gap-12">
          <FadeUp className="lg:col-span-2">
            <Overline>{t("detail.about")}</Overline>
            <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">{model.description}</p>
            <ul className="mt-8 space-y-3">
              {model.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-slate-700">
                  <Check className="w-5 h-5 text-[#0052CC] mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-3">
            <Overline>{t("detail.specs")}</Overline>
            <div className="mt-4 border border-slate-200" data-testid="specs-table">
              <Table>
                <TableBody>
                  {model.specs.map(([k, v]) => (
                    <TableRow key={k} className="border-slate-200">
                      <TableCell className="font-semibold text-[#0A2540] w-1/2 py-4 px-6">{k}</TableCell>
                      <TableCell className="text-slate-600 py-4 px-6">{v}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              {t("detail.disclaimer")}
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Gallery / Media */}
      {model.gallery && model.gallery.length > 0 && (
        <section className="pb-20 md:pb-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <FadeUp>
              <Overline>{t("detail.photos")}</Overline>
            </FadeUp>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="model-gallery">
              {model.gallery.map((src, i) => (
                <FadeUp key={src} delay={i * 0.06}>
                  <div className="border border-slate-200 bg-[#F4F7F9] overflow-hidden">
                    <img src={src} alt={`${model.name} Bild ${i + 1}`} loading="lazy"
                         className="w-full aspect-[16/10] object-cover hover:scale-[1.02] transition-transform duration-500" />
                  </div>
                </FadeUp>
              ))}
            </div>
            <div className="mt-6 grid grid-cols-1 gap-6" data-testid="video-section">
              {model.video && (
                <video
                  src={model.video}
                  poster={model.image}
                  controls
                  preload="metadata"
                  data-testid="model-video"
                  className="w-full border border-slate-200 bg-black aspect-video"
                />
              )}
              {(model.youtube_videos || (model.youtube ? [model.youtube] : [])).map((vid) => (
                <iframe
                  key={vid}
                  src={`https://www.youtube-nocookie.com/embed/${vid}`}
                  title={`${model.name} – Offizielles Video`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid={`model-youtube-${vid}`}
                  className="w-full border border-slate-200 aspect-video"
                />
              ))}
              {!model.video && !(model.youtube_videos || []).length && !model.youtube && (
                <div className="flex items-center gap-3 border border-dashed border-slate-300 p-6 text-slate-500 text-sm"
                     data-testid="video-placeholder">
                  <Play className="w-5 h-5" /> {t("detail.videoSoon")}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-[#0A2540] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">
            {t("detail.ctaTitle", { name: model.name })}
          </h2>
          <Button asChild size="lg" className="rounded-md bg-white text-[#0A2540] hover:bg-slate-100 px-8 py-3">
            <Link to={`/probefahrt?modell=${model.slug}`} data-testid="detail-bottom-cta-btn">
              {t("detail.ctaBtn")}
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
